import { api } from '@/lib/api';

export type IntegrationRunKind =
  | 'TITULOS_ERP'
  | 'LOCACOES'
  | 'OFICINA_API'
  | 'OFICINA_CSV'
  | 'DISTRIBUICAO'
  | 'CSV_TITULOS';

export type IntegrationRunTrigger = 'CRON' | 'MANUAL' | 'AUTO_POS_IMPORT';
export type IntegrationRunStatus = 'running' | 'success' | 'error' | 'partial';

export interface IntegrationRun {
  id: string;
  kind: IntegrationRunKind;
  trigger: IntegrationRunTrigger;
  status: IntegrationRunStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  companyId: string | null;
  userId: string | null;
  correlationId: string | null;
  processed: number | null;
  created: number | null;
  updated: number | null;
  errors: number | null;
  distributed: number | null;
  message: string | null;
  errorSummary: string | null;
  details: unknown;
  createdAt: string;
}

export interface IntegrationStats {
  days: number;
  totals: {
    runs: number;
    success: number;
    error: number;
    partial: number;
    running: number;
    successRate: number;
    processed: number;
    distributed: number;
  };
  byKind: Array<{
    kind: IntegrationRunKind;
    runs: number;
    success: number;
    error: number;
    processed: number;
    avgDurationMs: number | null;
  }>;
  daily: Array<{
    date: string;
    kind: IntegrationRunKind;
    processed: number;
    runs: number;
    success: number;
    avgDurationMs: number | null;
  }>;
  duration: { p50: number | null; p95: number | null };
  lastByKind: Partial<Record<IntegrationRunKind, IntegrationRun>>;
  lastDistributionOperators: Array<{ name: string; titlesCount: number; clientsCount?: number }> | null;
}

export const KIND_LABELS: Record<IntegrationRunKind, string> = {
  TITULOS_ERP: 'Títulos ERP',
  LOCACOES: 'Locações',
  OFICINA_API: 'Oficina API',
  OFICINA_CSV: 'Oficina CSV',
  DISTRIBUICAO: 'Distribuição',
  CSV_TITULOS: 'CSV Títulos',
};

export async function fetchIntegrationRuns(params: {
  kind?: IntegrationRunKind | '';
  status?: IntegrationRunStatus | '';
  correlationId?: string;
  page?: number;
  limit?: number;
}): Promise<{ items: IntegrationRun[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams();
  if (params.kind) qs.set('kind', params.kind);
  if (params.status) qs.set('status', params.status);
  if (params.correlationId) qs.set('correlationId', params.correlationId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await api.get(`/import/integration-runs?${qs.toString()}`);
  return res.data.data;
}

export async function fetchIntegrationStats(days = 7): Promise<IntegrationStats> {
  const res = await api.get(`/import/integration-stats?days=${days}`);
  return res.data.data;
}

/** Encerra um IntegrationRun em status running (libera lock do tipo). */
export async function killIntegrationRun(id: string): Promise<IntegrationRun> {
  const res = await api.post(`/import/integration-runs/${encodeURIComponent(id)}/kill`);
  return res.data.data.run as IntegrationRun;
}
